import classNames from 'classnames'
import Image from 'next/image'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import BottomSheet from '../../commonComponents/BottomSheet'
import { AdvertiserButton } from '../../commonComponents/hire/AdvertiserButton'

/**
 * A **generic** bottom-sheet with:
 *   • optional header image
 *   • title & description
 *   • two action buttons
 *
 * All structural classes reproduce the current UI 1-for-1.
 * Override anything via the `classNames` props below without side-effects.
 */
const ImageActionSheet = ({
  /* Bottom-sheet controls */
  isOpen,
  isHeaderPresent = true,
  showCloseIcon = false,
  onClose,
  onOpen = () => {},

  /* Visuals */
  headerImage, //   /public/… or remote URL
  title = '',
  description = '',

  /* Button config */
  primary = {
    label: 'Primary',
    onClick: () => {},
    className: '',
    loading: false,
    disabled: false
  },
  secondary = {
    label: 'Secondary',
    onClick: () => {},
    className: '',
    loading: false,
    disabled: false
  },

  /* Class overrides */
  sheetClassName = '',
  headerImageClassName = '',
  titleClassName = '',
  descClassName = '',
  actionsClassName = ''
}) => (
  <BottomSheet
    isOpen={isOpen}
    onClose={onClose}
    onOpen={onOpen}
    title="" /* header handled internally */
    customClassName={sheetClassName}
    isHeaderPresent={isHeaderPresent}
  >
    {/* Header image */}
    {headerImage && (
      <div className={classNames('aspect-[360/200] w-full', headerImageClassName)}>
        <Image
          src={headerImage}
          alt={typeof title === 'string' ? title : ''}
          className="h-full w-full"
          priority
        />
      </div>
    )}

    {showCloseIcon && (
      <div
        className="absolute top-5 right-5 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-white"
        onClick={onClose}
      >
        <CloseRoundedIcon className="h-6 w-6 text-[#191919]" />
      </div>
    )}

    {/* Copy block */}
    <div className="mx-[24px] my-[20px] flex flex-col gap-[16px] text-left">
      {title && (
        <div
          className={classNames(
            'text-[18px] font-normal leading-[27px] text-[#191919]',
            titleClassName
          )}
        >
          {title}
        </div>
      )}

      {description && (
        <div
          className={classNames(
            'text-[16px] font-normal leading-[21px] text-[#4b4b4b]',
            descClassName
          )}
        >
          {description}
        </div>
      )}
    </div>

    {/* Action buttons */}
    <div
      className={classNames(
        'flex w-full flex-col gap-[8px] border-t-[1px] border-t-[#e5e5e5] bg-[#ffffff] p-[16px]',
        actionsClassName
      )}
    >
      <AdvertiserButton
        label={primary.label}
        onClick={primary.onClick}
        loading={primary.loading}
        disabled={primary.disabled}
        className={classNames(
          'h-[48px] w-full rounded-[8px] border-none bg-[#ffbb00] text-base font-bold leading-[20px] text-[#191919]',
          primary.className
        )}
      />

      <AdvertiserButton
        label={secondary.label}
        onClick={secondary.onClick}
        loading={secondary.loading}
        disabled={secondary.disabled}
        className={classNames(
          'h-[48px] w-full border-none bg-[#ffffff] text-base font-bold leading-[20px] text-[#0174d3]',
          secondary.className
        )}
      />
    </div>
  </BottomSheet>
)

export default ImageActionSheet
